import {Component, Inject, Input, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Moment} from 'moment';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MainDataModels} from 'src/app/models/maindata-models';
import {ServiceAppModels} from 'src/app/models/serviceapp-models';
import {DatumFormatPipe} from 'src/app/pipes/datum-format.pipe';
import {MessageBoxService} from 'src/app/services/message-box.service';
import {ObjektDataService} from 'src/app/services/objekt-data.service';
import {ServiceAppService} from 'src/app/services/service-app.service';
import {SwissSettingsService} from 'src/app/services/swiss-settings.service';
import {DataService} from 'src/app/time-app/services/data.service';

@Component({
  selector: 'app-einsatz-eintrag',
  templateUrl: './einsatz-eintrag.component.html',
  styleUrls: ['./einsatz-eintrag.component.scss'],
})
export class EinsatzEintragComponent implements OnInit {
  @Input() formGroupEinsatz: UntypedFormGroup;
  public cardTitle: string = 'MAIN_APP_DIALOG.INPUT_DIALOG.NEW_USE.title';
  private assignment: ServiceAppModels.Assignment;
  objekte: Array<MainDataModels.SearchFieldResponseObjekt> = []; // Leeres Objekte-Array erstellen (SearchFieldResponseObjekt[])
  filteredDescriptions?: Observable<MainDataModels.SearchFieldResponseObjekt[]>;
  public errormsg: string = '';
  public hasError: boolean = false;
  public canClose: string = 'no';

  constructor(
    private swissSettings: SwissSettingsService,
    private svcObjektData: ObjektDataService,
    private _datumFormatPipe: DatumFormatPipe,
    private svcMessageBox: MessageBoxService,
    private svcSA: ServiceAppService,
    public dialogRef: MatDialogRef<EinsatzEintragComponent>,
    private dataService: DataService,
    @Inject(MAT_DIALOG_DATA) public data: ServiceAppModels.Assignment
  ) {
    this.assignment = data;

    this.formGroupEinsatz = new UntypedFormGroup({
      fldObjekt: new UntypedFormControl(),
      fldOrt: new UntypedFormControl(),
      fldDatum: new UntypedFormControl(),
      fldStartZeit: new UntypedFormControl(),
      fldEndZeit: new UntypedFormControl(),
    });
  }

  ngOnInit(): void {
    if (this.assignment.day)
      this.formGroupEinsatz.get('fldDatum')!.setValue(this.assignment.day);
    if (this.assignment.startTime)
      this.formGroupEinsatz
        .get('fldStartZeit')!
        .setValue(
          this._datumFormatPipe.transformOnlyTime(this.assignment.startTime)
        );
    if (this.assignment.endTime)
      this.formGroupEinsatz
        .get('fldEndZeit')!
        .setValue(
          this._datumFormatPipe.transformOnlyTime(this.assignment.endTime)
        );

    // Objekte für Suche laden
    this.dataService.getAllOrders().then(orders => {
      this.objekte = [];
      orders.forEach(o => this.objekte.push({beschreibung: o.objectSummary, objectID: o.objectID}));
      this.filteredDescriptions = this.formGroupEinsatz.controls['fldObjekt'].valueChanges.pipe(startWith(''), map((value) => this._filter(value))
      );
    })
  }

  getObjektDesc(item: MainDataModels.SearchFieldResponseObjekt) {
    if (item) {
      return item.beschreibung;
    } else return '';
  }

  // Suchelement wurde angeklickt
  selectedSearchItem(event: any) {
    if (this.swissSettings.getWebConfig().debugMode)
      console.log(event.option.value);
    if (event) {
      if (this.assignment) {
        this.assignment.objectID = event.option.value.objectID;
      }
    }
  }

  private _filter(value: string): MainDataModels.SearchFieldResponseObjekt[] {
    if (value && typeof value === 'string') {
      const filterValue = value.toLowerCase();

      if (this.objekte && this.objekte.length > 0) {
        // Sternsuche
        if (filterValue.length > 1 && filterValue[0] === '*') {
          const searchValue = filterValue.substring(1, filterValue.length);
          return this.objekte.filter(
            (objekt) =>
              objekt.beschreibung &&
              objekt.beschreibung.toLowerCase().indexOf(searchValue) >= 0
          );
        } else {
          return this.objekte.filter(
            (objekt) =>
              objekt.beschreibung &&
              objekt.beschreibung.toLowerCase().indexOf(filterValue) === 0
          );
        }
      } else return this.objekte;
    } else {
      return [];
    }
  }

  closeDialog() {
    this.dialogRef.close(null);
  }

  save() {
    // Neuer Einsatz wird in die Einsatzplanung gespeichert
    if (this.swissSettings.getWebConfig().debugMode) {
      console.log('FormValues: ', this.formGroupEinsatz.value);
    }

    try {
      let datumValue = this.formGroupEinsatz.controls['fldDatum'].value;

      let year: number = 0;
      let month: number = 0;
      let day: number = 0;

      if (datumValue instanceof Date) {
        let dtValue: Date = this.formGroupEinsatz.controls['fldDatum'].value;
        year = dtValue.getFullYear();
        month = dtValue.getMonth();
        day = dtValue.getDate();
      } else {
        // Moment
        let mom: Moment = this.formGroupEinsatz.controls['fldDatum'].value;
        year = mom.year();
        month = mom.month();
        day = mom.date();
      }
      let datum = new Date(year, month, day, 0, 0, 0);

      // Korrektur Zeitzone
      let offSetMin = -1 * datum.getTimezoneOffset();
      let offSetHour = offSetMin / 60;
      datum.setMinutes(offSetMin); // Korrektur der Minuten wegen der Zeitzone

      // Start-Datum
      this.assignment.day = datum;

      // Start-Zeit
      let startZeit = this.formGroupEinsatz.controls['fldStartZeit'].value;
      let hours = Number(startZeit.substring(0, 2));
      let minutes = Number(startZeit.substring(3, 5));
      let datumVon = new Date(year, month, day, hours + offSetHour, minutes, 0);
      this.assignment.startTime = datumVon;

      // End-Zeit
      let endZeit = this.formGroupEinsatz.controls['fldEndZeit'].value;
      let hoursBis = Number(endZeit.substring(0, 2));
      let minutesBis = Number(endZeit.substring(3, 5));
      let datumBis = new Date(
        year,
        month,
        day,
        hoursBis + offSetHour,
        minutesBis,
        0
      );
      this.assignment.endTime = datumBis;

      if (this.assignment.startTime >= this.assignment.endTime) {
        return this.svcMessageBox.showError(
          'Startzeit muss vor Endzeit liegen',
          '!! FEHLER !!'
        );
      }

      if (this.swissSettings.getWebConfig().debugMode) {
        console.log('Assignment: ', this.assignment);
      }

      this.svcSA.addEmpWorkPlanEntry(this.assignment).subscribe({
        next: (res) => {
          // Der Agena mitteilen, dass dies der zuletzt verwendete Termin ist
          this.svcSA.saveChoosedAppointmentToDB(this.assignment.rowID);
          this.dialogRef.close(this.assignment);
        },
        error: (error) => {
          this.svcMessageBox.showError(
            'Der Einsatz konnte nicht gespeichert werden. Meldung: ' + error,
            '!! FEHLER !!'
          );
        },
      });
    } catch (err) {
      this.svcMessageBox.showError(
        'Der Einsatz konnte nicht gespeichert werden. Ausnahme: ' + err,
        '!! FEHLER !!'
      );
    }
  }
}
